import React, { useEffect, useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { Button, Checkbox, Input, Select, Tooltip } from 'antd';
import { informationCreateSchema, InformationCreateFormData } from '@/schemas/informationSchema';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { HOUR_OPTIONS, MINUTE_OPTIONS } from '@/constants/timeOptions';
import { useTenantSiteTypes } from '@/hooks/admin/useTenantSiteTypes';
import { useInformationClinicOptions } from '@/hooks/admin/useInformationClinicOptions';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import { AppSelect, AppAdminFormLabel } from '@/components/atoms';
import { convertSeirekiWrap } from '@/utils/wareki';
import { partsToTime, timeToParts } from '@/utils/timeParts';
import useAppToast from '@/hooks/useAppToast';

const { TextArea } = Input;

interface AdminInformationCreateFormProps {
  siteType: string;
  onSubmit: (data: InformationCreateFormData) => void;
  isSubmitting?: boolean;
  onPreview?: (data: { title: string; information_date: string; contents: string | null }) => void;
}

const AdminInformationCreateForm: React.FC<AdminInformationCreateFormProps> = ({
  siteType,
  onSubmit,
  isSubmitting = false,
  onPreview,
}) => {
  const toast = useAppToast();
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useTrimmedForm<InformationCreateFormData>({
    resolver: zodResolver(informationCreateSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      title: '',
      information_date: '',
      contents: '',
      display_from_date: null,
      display_from_time: null,
      display_to_date: null,
      display_to_time: null,
      is_display: true,
      service_type_a2: true,
      service_type_r2: true,
      tenant_ids: [],
      clinic_ids: [],
    },
  });

  const tenantQueryScope = siteType === 'super-admin' ? undefined : siteType;

  const { data: tenantSiteTypesData } = useTenantSiteTypes(tenantQueryScope);
  const tenantItems = tenantSiteTypesData?.data?.site_types ?? [];

  const tenantOptions = useMemo(
    () => tenantItems.map(item => ({ value: item.id, label: item.name })),
    [tenantItems]
  );

  const showTenantSelector = siteType === 'super-admin' || siteType === 'medix';

  useEffect(() => {
    if (tenantItems.length === 0) return;
    const currentTenantIds = watch('tenant_ids');
    if (currentTenantIds.length > 0) return;
    if (!showTenantSelector) {
      const matched = tenantItems.find(t => t.site_type === siteType);
      if (matched) setValue('tenant_ids', [matched.id]);
    } else if (siteType === 'medix') {
      const defaults = tenantItems
        .filter(t => t.site_type === 'medix' || t.site_type === 'upsystem')
        .map(t => t.id);
      if (defaults.length > 0) setValue('tenant_ids', defaults);
    }
    // super-admin: leave empty
  }, [tenantItems, showTenantSelector, siteType, setValue, watch]);

  // Watch selected tenant_ids to control clinic selector
  const selectedTenantIds = watch('tenant_ids');

  // Infinite-scroll clinic options filtered by selected tenants
  const {
    data: clinicPages,
    isFetching: isLoadingClinics,
    fetchNextPage,
    hasNextPage,
  } = useInformationClinicOptions(siteType, selectedTenantIds, {
    enabled: selectedTenantIds.length > 0,
  });

  // Flatten all loaded pages into a single list
  const allClinicOptions = useMemo(
    () => clinicPages?.pages.flatMap(p => p.data) ?? [],
    [clinicPages]
  );

  // Accumulate clinic → site_type mapping across all loaded pages (needed for unselect logic)
  const clinicSiteTypeMapRef = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    allClinicOptions.forEach(c => {
      clinicSiteTypeMapRef.current.set(c.id, c.site_type ?? '');
    });
  }, [allClinicOptions]);

  const clinicSelectOptions = useMemo(
    () => allClinicOptions.map(c => ({ value: c.id, label: c.clinic_number })),
    [allClinicOptions]
  );

  // Track previous tenant selection to detect removals
  const prevTenantIdsRef = useRef<number[]>([]);
  useEffect(() => {
    const prev = prevTenantIdsRef.current;
    const curr = selectedTenantIds;
    const removedTenantIds = prev.filter(id => !curr.includes(id));

    if (removedTenantIds.length > 0) {
      const removedSiteTypes = new Set(
        tenantItems.filter(t => removedTenantIds.includes(t.id)).map(t => t.site_type)
      );
      if (removedSiteTypes.size > 0) {
        const currentClinicIds = watch('clinic_ids') ?? [];
        const filtered = currentClinicIds.filter(id => {
          const st = clinicSiteTypeMapRef.current.get(id);
          return !st || !removedSiteTypes.has(st);
        });
        if (filtered.length !== currentClinicIds.length) {
          setValue('clinic_ids', filtered);
        }
      }
    }

    prevTenantIdsRef.current = curr;
  }, [selectedTenantIds, tenantItems, setValue, watch]);

  const handleClinicPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;
    if (nearBottom && hasNextPage && !isLoadingClinics) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) return;
      clearErrors(name as keyof InformationCreateFormData);
      // Clear refine errors that depend on this field
      if (name === 'service_type_r2') clearErrors('service_type_a2');
      if (name === 'display_from_date') clearErrors('display_from_time');
      if (name === 'display_to_date') clearErrors('display_to_time');
      if (
        ['display_from_date', 'display_from_time', 'display_to_date', 'display_to_time'].includes(
          name
        )
      ) {
        clearErrors('display_to_date');
        clearErrors('display_from_date');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  // Watch date fields to control time field disabled state
  const displayFromDate = watch('display_from_date');
  const displayToDate = watch('display_to_date');

  useEffect(() => {
    if (!displayFromDate) setValue('display_from_time', null);
  }, [displayFromDate, setValue]);

  useEffect(() => {
    if (!displayToDate) setValue('display_to_time', null);
  }, [displayToDate, setValue]);

  const renderError = (fieldName: string) => {
    const parts = fieldName.split('.');
    let error: any = errors;
    for (const part of parts) {
      error = error?.[part];
    }
    if (!error) return null;
    return <div className="text-red-500 text-sm mt-1">{error.message as string}</div>;
  };

  return (
    <form
      onSubmit={e => {
        const currentTitle = getValues('title') ?? '';
        const trimmedTitle = currentTitle.trim();
        if (currentTitle !== trimmedTitle) {
          setValue('title', trimmedTitle, { shouldDirty: true });
        }
        void handleSubmit(onSubmit, () => toast.error('エラーが発生しました。'))(e);
      }}
      className="space-y-6"
    >
      {onPreview && (
        <div className="mb-4">
          <Button
            type="primary"
            onClick={() => {
              const values = watch();
              onPreview({
                title: values.title,
                information_date: values.information_date,
                contents: values.contents ?? null,
              });
            }}
          >
            プレビュー
          </Button>
        </div>
      )}

      {/* #4 表示対象 — hidden for single-tenant routes (e.g. hitowa), auto-set via useEffect */}
      {showTenantSelector && (
        <div>
          <AppAdminFormLabel label="表示対象" required />
          <Controller
            name="tenant_ids"
            control={control}
            render={({ field }) => (
              <Select
                mode="multiple"
                value={field.value}
                onChange={val => field.onChange(val)}
                options={tenantOptions}
                placeholder="表示対象"
                className="w-full"
                status={errors.tenant_ids ? 'error' : undefined}
                aria-label="表示対象"
                maxTagCount="responsive"
              />
            )}
          />
          {renderError('tenant_ids')}
        </div>
      )}

      {/* #5 表示サービス + #14 表示設定 — same row, 2 cols */}
      <div className="grid grid-cols-2 items-start">
        <div>
          <AppAdminFormLabel label="表示サービス" required />
          <div className="flex gap-6">
            <Controller
              name="service_type_a2"
              control={control}
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  {SERVICE_TYPE_A2} (はり・きゅう)
                </Checkbox>
              )}
            />
            <Controller
              name="service_type_r2"
              control={control}
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  {SERVICE_TYPE_R2} (マッサージ)
                </Checkbox>
              )}
            />
          </div>
          {renderError('service_type_a2')}
        </div>

        <div>
          <AppAdminFormLabel label="表示設定" />
          <Controller
            name="is_display"
            control={control}
            render={({ field }) => (
              <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)} />
            )}
          />
        </div>
      </div>

      {/* #6 対象治療院ID */}
      <div>
        <AppAdminFormLabel label="対象治療院ID" />
        <Controller
          name="clinic_ids"
          control={control}
          render={({ field }) => (
            <Select
              mode="multiple"
              value={field.value}
              onChange={val => field.onChange(val)}
              options={clinicSelectOptions}
              loading={isLoadingClinics}
              disabled={selectedTenantIds.length === 0}
              placeholder={selectedTenantIds.length === 0 ? 'サイトを先に選択してください' : ''}
              className="w-full"
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
              onPopupScroll={handleClinicPopupScroll}
              aria-label="対象治療院ID"
              maxTagCount="responsive"
              maxTagPlaceholder={omittedValues => {
                const allLabels = field.value
                  .map(id => clinicSelectOptions.find(o => o.value === id)?.label)
                  .filter(Boolean)
                  .join(', ');
                return (
                  <Tooltip title={allLabels}>
                    <span>+{omittedValues.length} ...</span>
                  </Tooltip>
                );
              }}
            />
          )}
        />
      </div>

      {/* #7 タイトル + #8 お知らせ日 — same row, 2 cols */}
      <div className="grid grid-cols-2 items-start gap-6">
        <div>
          <AppAdminFormLabel label="タイトル" required />
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input {...field} status={errors.title ? 'error' : undefined} aria-label="タイトル" />
            )}
          />
          {renderError('title')}
        </div>

        <div>
          <AppAdminFormLabel label="お知らせ日" required />
          <Controller
            name="information_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || ''}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  field.onChange(seireki || '');
                }}
                picker="day"
                className="w-full"
                placeholder=""
                error={!!errors.information_date}
              />
            )}
          />
          {renderError('information_date')}
        </div>
      </div>

      {/* #9 お知らせ内容 */}
      <div>
        <AppAdminFormLabel label="お知らせ内容" />
        <Controller
          name="contents"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              value={field.value ?? ''}
              rows={10}
              status={errors.contents ? 'error' : undefined}
              aria-label="お知らせ内容"
            />
          )}
        />
        {renderError('contents')}
      </div>

      {/* #10-13 有効期限 */}
      <div>
        <AppAdminFormLabel label="有効期限" />
        <div className="flex items-center gap-2">
          <Controller
            name="display_from_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || ''}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  field.onChange(seireki || null);
                }}
                picker="day"
                className="flex-1"
                placeholder=""
                error={!!errors.display_from_date}
              />
            )}
          />
          <Controller
            name="display_from_time"
            control={control}
            render={({ field }) => {
              const parts = timeToParts(field.value);
              return (
                <div className="flex items-center gap-1">
                  <AppSelect
                    className="min-w-[56px]"
                    options={HOUR_OPTIONS}
                    value={parts != null ? parts.hour : ''}
                    onChange={v => {
                      const h = v === '' ? null : Number(v);
                      field.onChange(partsToTime(h, parts?.minute ?? 0));
                    }}
                    disabled={!displayFromDate}
                    status={errors.display_from_time ? 'error' : undefined}
                  />
                  <span className="text-gray-500">:</span>
                  <AppSelect
                    className="min-w-[56px]"
                    options={MINUTE_OPTIONS}
                    value={parts != null ? parts.minute : ''}
                    onChange={v => {
                      const m = v === '' ? null : Number(v);
                      field.onChange(partsToTime(parts?.hour ?? 0, m));
                    }}
                    disabled={!displayFromDate}
                    status={errors.display_from_time ? 'error' : undefined}
                  />
                </div>
              );
            }}
          />
          <span>-</span>
          <Controller
            name="display_to_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || ''}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  field.onChange(seireki || null);
                }}
                picker="day"
                className="flex-1"
                placeholder=""
                error={!!errors.display_to_date}
              />
            )}
          />
          <Controller
            name="display_to_time"
            control={control}
            render={({ field }) => {
              const parts = timeToParts(field.value);
              return (
                <div className="flex items-center gap-1">
                  <AppSelect
                    className="min-w-[56px]"
                    options={HOUR_OPTIONS}
                    value={parts != null ? parts.hour : ''}
                    onChange={v => {
                      const h = v === '' ? null : Number(v);
                      field.onChange(partsToTime(h, parts?.minute ?? 0));
                    }}
                    disabled={!displayToDate}
                    status={errors.display_to_time ? 'error' : undefined}
                  />
                  <span className="text-gray-500">:</span>
                  <AppSelect
                    className="min-w-[56px]"
                    options={MINUTE_OPTIONS}
                    value={parts != null ? parts.minute : ''}
                    onChange={v => {
                      const m = v === '' ? null : Number(v);
                      field.onChange(partsToTime(parts?.hour ?? 0, m));
                    }}
                    disabled={!displayToDate}
                    status={errors.display_to_time ? 'error' : undefined}
                  />
                </div>
              );
            }}
          />
        </div>
        {renderError('display_from_date')}
        {renderError('display_from_time')}
        {renderError('display_to_time')}
        {renderError('display_to_date')}
      </div>

      {/* #15 更新 */}
      <div className="flex justify-end">
        <Button type="primary" htmlType="submit" loading={isSubmitting} className="min-w-[120px]">
          更新
        </Button>
      </div>
    </form>
  );
};

export default AdminInformationCreateForm;
